import { useContext, useEffect, useState } from "react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { cx } from "shared/utils";
import { VerifiedIncomeReport } from "types/landlord/reports";
import { ChevronDown, Download01 } from "untitledui-js-base";

import { Section } from "views/applications-id-report/components/Section";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";
import { DocumentCard } from "views/applications-id-report/slices/IncomeReportSlice/components/DocumentCard";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";

interface DocumentWarningsProps {
  report?: VerifiedIncomeReport;
  executeScrollToHA: () => void;
  executeScrollToOfferLetter: () => void;
}

const OCROLUS_FAIL_STATUSES = ["UNABLE_TO_PROCESS", "UNSUPPORTED"];

export const DocumentWarnings = ({
  report,
  executeScrollToHA,
  executeScrollToOfferLetter
}: DocumentWarningsProps) => {
  const { document, manual_review } = report || {};
  const { documentPrintingState } = useContext(PrintAllContext);

  const [open, setIsOpen] = useState(false);

  const isPrinting = Boolean(documentPrintingState?.length);

  useEffect(() => {
    if (isPrinting) setIsOpen(true);
  }, [isPrinting]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!document?.warnings?.length && !manual_review?.length) return null;

  return (
    <Section sectionTitle="Warnings" maxWidth="none">
      <div className="flex flex-col gap-8">
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
        {document?.warnings?.map((warning) => {
          const docWasRejected = OCROLUS_FAIL_STATUSES.includes(
            warning.detect_status
          );

          const status = parseDocStatus(
            docWasRejected
              ? 0
              : warning.form_analysis[0]?.form_authenticity?.score || 0
          );

          return (
            <div key={warning.uploaded_doc_uuid}>
              <div className="flex w-full justify-between flex-wrap md:flex-nowrap gap-y-2">
                <div className="flex items-center gap-3">
                  <div
                    className={cx(
                      "text-base shrink-0 font-bold h-5 w-5 rounded-full flex items-center justify-center",
                      status === "Warning" && "bg-warning-500 text-warning-50",
                      status === "Rejected" && "bg-danger-500 text-white"
                    )}
                  >
                    !
                  </div>
                  <div className="flex flex-col gap-1 items-start">
                    <p className="text-content-1000 font-medium">
                      {status === "Warning" &&
                        "We recommend manually reviewing the document as suspicious signals were found"}

                      {status === "Rejected" &&
                        `Uploaded document rejected: ${warning.doc_info?.rejection_reason}`}
                    </p>
                    <p className="text-content-400 font-normal">
                      {warning.asset?.asset?.filename}
                    </p>
                  </div>
                </div>

                {docWasRejected ? (
                  <a
                    href={warning.asset?.asset?.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      variant={ButtonVariant.BASE}
                      size={ButtonSize.SM}
                      Icon={Download01}
                    >
                      Download document
                    </Button>
                  </a>
                ) : (
                  <Button
                    variant={ButtonVariant.BASE}
                    size={ButtonSize.SM}
                    onClick={() => setIsOpen((prev) => !prev)}
                  >
                    <div className="flex items-center gap-2">
                      <ChevronDown
                        className={cx(
                          "stroke-content-300",
                          open ? "rotate-180 transition-all" : ""
                        )}
                        size="20"
                      />
                      {open ? "Hide details" : "Show details"}
                    </div>
                  </Button>
                )}
              </div>

              <Show if={!docWasRejected && open}>
                <div className="mt-4 p-4 bg-background-900 border border-alpha-100">
                  <DocumentCard
                    ignorePrint
                    score={
                      warning.form_analysis[0]?.form_authenticity?.score || 0
                    }
                    asset={warning.asset?.asset}
                    reason_codes={
                      warning.form_analysis[0]?.form_authenticity?.reason_codes
                    }
                  />
                </div>
              </Show>
            </div>
          );
        })}

        {manual_review?.map((manualReview, idx) => {
          return (
            <div
              key={manualReview.type + idx}
              className="flex w-full justify-between"
            >
              <div className="flex items-center gap-3">
                <div
                  className="text-base shrink-0 font-bold h-5 w-5 rounded-full flex items-center justify-center
                  bg-warning-500 text-warning-50"
                >
                  !
                </div>
                <div className="flex flex-col gap-1 items-start">
                  <p className="text-content-1000 font-medium">
                    The document requires manual review
                  </p>
                  <p className="text-content-400 font-normal">
                    {manualReview.asset?.filename} - {manualReview.type}
                  </p>
                </div>
              </div>

              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                onClick={
                  manualReview.type === "Housing assistance program"
                    ? executeScrollToHA
                    : executeScrollToOfferLetter
                }
              >
                Review
              </Button>
            </div>
          );
        })}
      </div>
    </Section>
  );
};
